import { AntDesign } from "@expo/vector-icons";
import { LayoutNodeType } from "@towersystems/roam-common/lib/generated-types";
import { useEffect, useState } from "react";
import { LayoutChangeEvent, View } from "react-native";
import {
  Colors,
  GridList,
  Spacings,
  TouchableOpacity,
} from "react-native-ui-lib";
import { DragContainer } from "../../../../components/DragDrop";
import Pager from "../../../../components/Pager/Pager";
import {
  PageItemType,
  getMaxTilePositionForPage,
  getPageNumberByPosition,
} from "../../../../utilities/layout";
import { useLayoutProvider } from "../../utils/layout";
import { DefaultQuickKeyList, QuickListChangePageBars } from "./components";
import { QuickListEmptyLayout } from "./components/QuickListEmptyLayout/QuickListEmptyLayout";
import { ItemRendererContext, MissingLayoutDimensionsError } from "./constants";
import {
  DynamicActionItemRenderer,
  FilterItemRenderer,
  PlaceholderItemRenderer,
  ProductItemRenderer,
} from "./renderers";
import { DynamicQuickKeyListV2 } from "./DynamicQuickKeyListV2";

// eventually change this to a dynamic list
// the idea is that when the user is searching, we show the filtered product list
// otherwise we show a dynamic grid which changes based on the current sale state
export const DynamicQuickKeyList = () => {
  const {
    layout,
    listRef,
    isEditing,
    pagesController,
    layoutDimensions,
    setWidth,
    getProductById,
    getNameFromFilter,
  } = useLayoutProvider();

  const [dragging, setDragging] = useState<boolean>(false);

  const { currentPage, pages, goToPage, addEmptyPage } = pagesController;

  function handleUnload() {
    listRef.current?.scrollToOffset({ offset: 0, animated: false });
  }

  useEffect(() => {
    window.onunload = handleUnload;
  }, []);

  if (!layout) {
    return <DefaultQuickKeyList />;
  }

  function getMaxTilePosition(item: PageItemType) {
    if (!layoutDimensions) throw new MissingLayoutDimensionsError();
    const pageNumber = getPageNumberByPosition(layoutDimensions, item.position);
    const page = pages[pageNumber - 1];

    if (!page) return 0;

    const maxTilePosition = getMaxTilePositionForPage(page);

    return maxTilePosition;
  }

  function renderPage({ route }: any) {
    const { key } = route;
    const item = pages.find((p) => p.page.toString() === key);
    if (!layoutDimensions || !item) throw new MissingLayoutDimensionsError();
    return (
      <GridList
        key={item.page}
        scrollEnabled={!dragging}
        contentContainerStyle={{
          position: "relative",
          width: "100%",
        }}
        keyExtractor={(item) => item.id}
        //scrollEnabled={!isEditing || !dragging}
        data={item.items ?? []}
        numColumns={layoutDimensions.columns}
        renderItem={(item) => (
          <ItemRendererContext.Provider
            key={item.index}
            value={{
              dragging,
              isEditing,
              setDragging: (d) => setDragging(d),
              maxTilePosition: getMaxTilePosition(item.item),
            }}
          >
            {renderItem(item)}
          </ItemRendererContext.Provider>
        )}
        containerWidth={layoutDimensions.width}
      ></GridList>
    );
  }

  const renderItem = ({ item }: { item: PageItemType }) => {
    if (item.placeholder) {
      return <PlaceholderItemRenderer key={item.id} {...{ item }} />;
    } else if (item.config?.action) {
      return <DynamicActionItemRenderer key={item.id} {...{ item }} />;
    } else if (item.config?.type === LayoutNodeType.PRODUCT) {
      return (
        <ProductItemRenderer
          key={item.id}
          {...{
            item,
            getProductById,
          }}
        />
      );
    } else if (item.config?.type === LayoutNodeType.FILTER) {
      return (
        <FilterItemRenderer key={item.id} {...{ item, getNameFromFilter }} />
      );
    }
  };

  function handleUpdateLayoutDimensions(e: LayoutChangeEvent) {
    const layoutEvent = e.nativeEvent.layout;
    setWidth(layoutEvent.width);
  }

  const containerStyle = {
    marginTop: Spacings.s7,
    display: "flex",
    flexGrow: 1,
    flexShrink: 1,
  } as const;

  const routes = pages.map((p) => ({
    key: p.page.toString(),
    title: "e",
  }));

  return (
    <View style={containerStyle} onLayout={handleUpdateLayoutDimensions}>
      <DynamicQuickKeyListV2 />
    </View>
  );
};
